/**
 * <s-mask-picker> — 1–4 mask quantity cards with a design dropdown per mask (snippets/mask-picker.liquid).
 *
 * - Only the selects inside the checked card are enabled, so only they are submitted.
 * - Clicking anywhere on a card selects it.
 * - The first dropdown follows the main design choice: radios with [data-design-pill] in the same
 *   form (homepage) or the variant picker re-render (product page, data-default-variant).
 * - The chosen count and extra designs survive the product-form partial re-render on variant change.
 */
const memory = { count: '1', designs: [] };

class SMaskPicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#restore();
    this.#sync();

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.name === 'mask_count') {
        this.#sync();
        this.#remember();
      } else if (target instanceof HTMLSelectElement && target.matches('[data-mask-select]')) {
        this.#mirrorSelect(target);
        this.#remember();
      }
    }, opts);

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element) || target.closest('select, label, input')) return;
      const radio = target.closest('[data-mask-card]')?.querySelector('input[name="mask_count"]');
      if (radio instanceof HTMLInputElement && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, opts);

    // Homepage design pills set the first mask's design.
    this.closest('form')?.addEventListener('change', (event) => {
      const target = event.target;
      if (target instanceof HTMLInputElement && target.matches('[data-design-pill]')) {
        this.#setFirstDesign(target.value);
        this.#remember();
      }
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #cards() {
    return [...this.querySelectorAll('[data-mask-card]')];
  }

  #sync() {
    for (const card of this.#cards()) {
      const checked = card.querySelector('input[name="mask_count"]')?.checked;
      const wrap = card.querySelector('[data-mask-selects]');
      if (wrap) wrap.hidden = !checked;
      card.querySelectorAll('[data-mask-select]').forEach((el) => { el.disabled = !checked; });
    }
    // Keep the form's single variant id (used for native fallback) on the first chosen design.
    const first = this.#activeSelects()[0];
    const idInput = this.closest('form')?.querySelector('input[name="id"]');
    if (first && idInput instanceof HTMLInputElement) idInput.value = first.value;
  }

  #activeSelects() {
    return [...this.querySelectorAll('[data-mask-select]:not([disabled])')];
  }

  // Same position across cards shares a design, so switching count keeps choices.
  #mirrorSelect(changed) {
    const index = [...changed.closest('[data-mask-card]').querySelectorAll('[data-mask-select]')].indexOf(changed);
    for (const card of this.#cards()) {
      const el = card.querySelectorAll('[data-mask-select]')[index];
      if (el && el !== changed) el.value = changed.value;
    }
    this.#sync();
  }

  #setFirstDesign(variantId) {
    for (const card of this.#cards()) {
      const el = card.querySelector('[data-mask-select]');
      if (el) el.value = variantId;
    }
    this.#sync();
  }

  #remember() {
    const checked = this.querySelector('input[name="mask_count"]:checked');
    memory.count = checked?.value || '1';
    const fullest = this.#cards().at(-1);
    memory.designs = [...(fullest?.querySelectorAll('[data-mask-select]') || [])].map((el) => el.value);
  }

  #restore() {
    const radio = this.querySelector(`input[name="mask_count"][value="${memory.count}"]`);
    if (radio instanceof HTMLInputElement) radio.checked = true;
    for (const card of this.#cards()) {
      card.querySelectorAll('[data-mask-select]').forEach((el, i) => {
        // The first design always follows the server-selected variant; later ones keep the shopper's picks.
        const value = i === 0 ? this.dataset.defaultVariant : memory.designs[i];
        if (value && el.querySelector?.(`option[value="${value}"]`)) el.value = value;
      });
    }
  }
}

if (!customElements.get('s-mask-picker')) {
  customElements.define('s-mask-picker', SMaskPicker);
}
